import { useEffect, useState } from 'react';
import { api } from '../api/client.js';

/**
 * Angaben, die der Export nicht liefert — am einzelnen Exemplar.
 *
 * Poke Genie kennt weder Shiny noch Dyna-Fähigkeit; beides steht nur im
 * Spiel. Gepflegt wird es deshalb hier, und der Import führt es über den
 * Fingerabdruck mit, statt es zu überschreiben.
 */
export default function ExemplarKonfig({ eintrag: e, onGeaendert, onSchliessen, onEntfernt }) {
  const [shiny, setShiny] = useState(e.isShiny ?? false);
  const [dynamax, setDynamax] = useState(e.isDynamax ?? false);
  const [gigamax, setGigamax] = useState(e.isGigamax ?? false);
  const [notiz, setNotiz] = useState(e.note ?? '');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState(null);

  // Löschen ist nicht rückgängig zu machen — deshalb ein zweiter Schritt
  // statt einer Rückfrage im Systemdialog, den man wegklickt.
  const [loeschFrage, setLoeschFrage] = useState(false);

  // Attacken: was diese Art lernen kann und was dieses Exemplar hat.
  const [attacken, setAttacken] = useState(null);
  const [schnell, setSchnell] = useState('');
  const [lade1, setLade1] = useState('');
  const [lade2, setLade2] = useState('');

  useEffect(() => {
    api.exemplarAttacken.holen(e.id).then((d) => {
      setAttacken(d.moeglich);
      setSchnell(d.gelernt.schnell ?? '');
      setLade1(d.gelernt.lade1 ?? '');
      setLade2(d.gelernt.lade2 ?? '');
    }).catch(() => setAttacken({ schnell: [], lade: [] }));
  }, [e.id]);

  /**
   * Exemplar entfernen.
   *
   * Gedacht für verschickte Pokémon: Bleiben sie in der Sammlung, rechnet
   * jede Auswertung mit Exemplaren, die es im Spiel nicht mehr gibt — die
   * Teamvorschläge stellen dann Pokémon auf, die nicht antreten können.
   */
  const entfernen = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.collection.remove(e.id);
      onEntfernt?.(e.id);
    } catch (err) {
      setFehler(err.message);
      setBusy(false);
    }
  };

  const speichern = async () => {
    setBusy(true);
    setFehler(null);
    try {
      const felder = { note: notiz, isShiny: shiny, isDynamax: dynamax, isGigamax: gigamax };
      await api.collection.update(e.id, felder);

      // Attacken getrennt: Sie hängen am Exemplar, nicht an den Marken.
      await api.exemplarAttacken.speichern(e.id, {
        schnell: schnell || null,
        lade1: lade1 || null,
        lade2: lade2 || null,
      });

      onGeaendert(e.id, { ...felder, note: notiz || null });
      onSchliessen();
    } catch (err) {
      setFehler(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Gigadynamax setzt Dynamax voraus — beides getrennt anzuhaken ergäbe
  // einen Zustand, den es im Spiel nicht gibt.
  const gigaUmschalten = (an) => {
    setGigamax(an);
    if (an) setDynamax(true);
  };

  return (
    <div className="konfig">
      <div className="konfig__kopf">
        <strong>{e.name}{e.form ? ' ' + e.form : ''}</strong>
        <span className="text-faint">WP {e.cp}</span>
      </div>

      <label className="konfig__zeile">
        <input type="checkbox" checked={shiny} onChange={(ev) => setShiny(ev.target.checked)} />
        <span>
          Schillernd
          <span className="konfig__hinweis">Steht nicht im Export</span>
        </span>
      </label>

      <label className="konfig__zeile">
        <input
          type="checkbox"
          checked={dynamax}
          disabled={gigamax}
          onChange={(ev) => setDynamax(ev.target.checked)}
        />
        <span>
          Dynamax
          <span className="konfig__hinweis">Aus einem Dyna-Kampf gefangen</span>
        </span>
      </label>

      <label className="konfig__zeile">
        <input type="checkbox" checked={gigamax} onChange={(ev) => gigaUmschalten(ev.target.checked)} />
        <span>
          Gigadynamax
          <span className="konfig__hinweis">Setzt Dynamax voraus</span>
        </span>
      </label>

      {attacken && (attacken.schnell.length > 0 || attacken.lade.length > 0) && (
        <div className="konfig__attacken">
          <span className="konfig__hinweis">
            Attacken — wird für die Raid-Teamauswahl verwendet
          </span>

          <select className="input" value={schnell} onChange={(ev) => setSchnell(ev.target.value)}>
            <option value="">Sofort-Attacke wählen</option>
            {attacken.schnell.map((m) => (
              <option key={m.id} value={m.name}>{m.name} ({m.typ})</option>
            ))}
          </select>

          <select className="input" value={lade1} onChange={(ev) => setLade1(ev.target.value)}>
            <option value="">Lade-Attacke wählen</option>
            {attacken.lade.map((m) => (
              <option key={m.id} value={m.name}>{m.name} ({m.typ})</option>
            ))}
          </select>

          <select className="input" value={lade2} onChange={(ev) => setLade2(ev.target.value)}>
            <option value="">Zweite Lade-Attacke (optional)</option>
            {attacken.lade.map((m) => (
              <option key={m.id} value={m.name}>{m.name} ({m.typ})</option>
            ))}
          </select>
        </div>
      )}

      <input
        className="input"
        value={notiz}
        maxLength={300}
        placeholder="Notiz zu diesem Exemplar"
        onChange={(ev) => setNotiz(ev.target.value)}
        onKeyDown={(ev) => ev.key === 'Enter' && speichern()}
      />

      {fehler && <div className="error">{fehler}</div>}

      <div className="inline">
        <button className="btn btn--primary" onClick={speichern} disabled={busy}>
          {busy ? 'Speichert…' : 'Speichern'}
        </button>
        <button className="btn btn--ghost" onClick={onSchliessen} disabled={busy}>
          Abbrechen
        </button>
      </div>

      {/* Entfernen — für verschickte Pokémon. Abgesetzt vom Rest, damit es
          nicht versehentlich neben "Speichern" getroffen wird. */}
      <div className="konfig__entfernen">
        {loeschFrage ? (
          <div className="inline">
            <span className="text-faint">Wirklich entfernen?</span>
            <button className="btn btn--warn btn--klein" onClick={entfernen} disabled={busy}>
              Ja, entfernen
            </button>
            <button
              className="btn btn--ghost btn--klein"
              onClick={() => setLoeschFrage(false)}
              disabled={busy}
            >
              Nein
            </button>
          </div>
        ) : (
          <button
            className="btn btn--ghost btn--klein text-faint"
            onClick={() => setLoeschFrage(true)}
            disabled={busy}
          >
            Aus der Sammlung entfernen
          </button>
        )}
      </div>
    </div>
  );
}
